import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Pagination from '../../../components/pagination/Pagination';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import { usePagination } from '../../../hooks/usePagination';
import { useResourceGridActions } from '../../../hooks/useResourceGridActions';
import { addPlanToStates } from '../../../services/plans/addPlanToStates';
import { getPlansList } from '../../../services/plans/listPlans';
import { removePlanFromStates } from '../../../services/plans/removePlanFromStates';
import {
  Insurance,
  InsurancePlan,
  PlansResponse,
} from '../../../types/plans/types';
import { plansColumns } from './plansColumns';

const SECTION_PAGE_SIZE = 5;

interface StatePlansSectionProps {
  stateName: string;
  stateAbbreviation: string;
  practiceCount: number;
  providerCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isManageOpen: boolean;
  onToggleManage: () => void;
  assignedPlans: Insurance[];
  allPlans: Insurance[];
  globalFilters: Record<string, string | string[]>;
  enhancedBadges: boolean;
  onPlansChanged: () => void;
  canManagePlans: boolean;
  catalogReady: boolean;
  // Fired once this section's own usePagination request transitions from
  // loading to settled - lets the parent's "Expand all" queue release the
  // next state only once a concurrency slot actually frees up, instead of
  // on a fixed timer that doesn't reflect whether anything finished.
  onSectionLoadSettled?: () => void;
}

const StatePlansSection: React.FC<StatePlansSectionProps> = ({
  stateName,
  stateAbbreviation,
  practiceCount,
  providerCount,
  isExpanded,
  onToggleExpand,
  isManageOpen,
  onToggleManage,
  assignedPlans,
  allPlans,
  globalFilters,
  enhancedBadges,
  onPlansChanged,
  canManagePlans,
  catalogReady,
  onSectionLoadSettled,
}) => {
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [planPendingRemoval, setPlanPendingRemoval] = useState<Insurance | null>(
    null
  );
  const cancelRef = useRef<HTMLButtonElement>(null);
  // Guards the actual start of a mutation synchronously, independent of
  // React's render/commit timing. pendingPlanId (state) is only for display
  // (spinners/disabled styling) - checking it alone at the top of a handler
  // isn't enough to rule out two rapid clicks both passing the check before
  // a re-render disables the controls, so this ref is the real gate.
  const mutationInFlightRef = useRef(false);
  const {
    isOpen: isRemoveConfirmOpen,
    onOpen: onOpenRemoveConfirm,
    onClose: onCloseRemoveConfirm,
  } = useDisclosure();

  const sectionFilters = useMemo(
    () => ({ ...globalFilters, state: [stateAbbreviation] }),
    [globalFilters, stateAbbreviation]
  );

  const fetchSectionPlans = useCallback(
    (startToken: string | null) =>
      getPlansList(startToken, sectionFilters, SECTION_PAGE_SIZE),
    [sectionFilters]
  );

  const {
    items: plans,
    loading,
    error: sectionError,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
    refetch: refetchSectionPlans,
  } = usePagination<InsurancePlan, PlansResponse>(
    fetchSectionPlans,
    SECTION_PAGE_SIZE,
    { onSkipped: onSectionLoadSettled },
    sectionFilters,
    isExpanded
  );

  // Report only the true->false edge (the request that was in flight just
  // finished), not every render where loading happens to be false already.
  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (wasLoadingRef.current && !loading) {
      onSectionLoadSettled?.();
    }
    wasLoadingRef.current = loading;
  }, [loading, onSectionLoadSettled]);

  const processedPlans = useMemo(() => {
    return (plans || [])
      .filter(
        (plan): plan is InsurancePlan =>
          Boolean(plan) && Array.isArray(plan.plans) && plan.plans.length > 0
      )
      .map((plan) => ({
        ...plan,
        plans: plan.plans
          .filter(
            (p) => p && p.insurance && p.insurance.id && p.insurance.friendlyName
          )
          .sort((a, b) =>
            (a.insurance?.friendlyName || '').localeCompare(
              b.insurance?.friendlyName || ''
            )
          ),
      }))
      .filter((plan) => plan.plans.length > 0);
  }, [plans]);

  const memoizedColumns = useMemo(
    () => plansColumns(processedPlans, enhancedBadges, []),
    [processedPlans, enhancedBadges]
  );

  // plansColumns always prepends an actions column, but ResourceGrid only
  // renders it when both actionMenuContext and actionMenuActions are given -
  // otherwise every row gets a blank first column and the Edit Provider
  // Credential Status action is lost. scopeType 'market' matches the
  // original /plans grid's context (it hides bulk-edit/view-plans, which
  // don't apply to this broad, multi-provider grid); scopeId is this
  // section's state since that's what it's actually filtered by here.
  const resourceGridContext = useMemo(
    () => ({
      scopeType: 'market' as const,
      scopeId: stateAbbreviation,
      activeFilters: sectionFilters,
      columnsMode: 'plans' as const,
    }),
    [stateAbbreviation, sectionFilters]
  );
  const resourceGridActions = useResourceGridActions(resourceGridContext);

  const availablePlans = useMemo(() => {
    const assignedIds = new Set(assignedPlans.map((p) => p.id));
    const term = searchTerm.trim().toLowerCase();
    return allPlans
      .filter((p) => !assignedIds.has(p.id))
      .filter((p) =>
        term
          ? [p.friendlyName, p.companyName, p.emrInsurancePlan].some((field) =>
              (field || '').toLowerCase().includes(term)
            )
          : true
      )
      .slice(0, 8);
  }, [allPlans, assignedPlans, searchTerm]);

  const handleAddPlan = async (plan: Insurance) => {
    // mutationInFlightRef is checked and set synchronously, before any
    // React re-render - otherwise two rapid clicks (this one or the remove
    // flow) could both read pendingPlanId as null and both start a mutation,
    // the second overwriting pendingPlanId while the first is still running.
    if (!canManagePlans || mutationInFlightRef.current) return;
    mutationInFlightRef.current = true;
    setPendingPlanId(plan.id);
    try {
      await addPlanToStates({
        planId: plan.id,
        states: [stateAbbreviation],
      });
      toast({
        title: `${plan.friendlyName} added to ${stateName}`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      setSearchTerm('');
      onPlansChanged();
      refetchSectionPlans?.();
    } catch (error) {
      toast({
        title: `Failed to add ${plan.friendlyName} to ${stateName}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      mutationInFlightRef.current = false;
      setPendingPlanId(null);
    }
  };

  const requestRemovePlan = (plan: Insurance) => {
    // Same reasoning as handleAddPlan - don't let a remove start (or even
    // open its confirm dialog) while another add/remove is still in flight.
    if (!canManagePlans || mutationInFlightRef.current) return;
    setPlanPendingRemoval(plan);
    onOpenRemoveConfirm();
  };

  const handleConfirmRemovePlan = async () => {
    const plan = planPendingRemoval;
    if (!plan || !canManagePlans || mutationInFlightRef.current) return;
    onCloseRemoveConfirm();
    mutationInFlightRef.current = true;
    setPendingPlanId(plan.id);
    try {
      await removePlanFromStates({
        planId: plan.id,
        states: [stateAbbreviation],
      });
      toast({
        title: `${plan.friendlyName} removed from ${stateName}`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      onPlansChanged();
      refetchSectionPlans?.();
    } catch (error) {
      toast({
        title: `Failed to remove ${plan.friendlyName} from ${stateName}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      mutationInFlightRef.current = false;
      setPendingPlanId(null);
      setPlanPendingRemoval(null);
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
      p="1rem"
    >
      <Flex align="center" justify="space-between" wrap="wrap" gap="0.75rem">
        <Flex align="baseline" gap="0.875rem" wrap="wrap">
          <Text fontSize="1.0625rem" fontWeight="700" color="background.brandDark">
            {stateName} ({stateAbbreviation})
          </Text>
          <Text fontSize="0.75rem" color="gray.500">
            <b style={{ color: '#2D3748' }}>{providerCount}</b> Providers &middot;{' '}
            <b style={{ color: '#2D3748' }}>{practiceCount}</b> Practices
          </Text>
        </Flex>
        <HStack spacing="1rem">
          {canManagePlans && (
            <Button
              size="sm"
              variant="outline"
              borderColor={isManageOpen ? 'blue.300' : 'gray.200'}
              bg={isManageOpen ? 'blue.50' : 'white'}
              color={isManageOpen ? 'blue.700' : 'gray.600'}
              onClick={onToggleManage}
              isDisabled={!catalogReady}
              title={
                catalogReady
                  ? undefined
                  : 'The plan catalog has not finished loading - this count is not yet reliable.'
              }
              rightIcon={isManageOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            >
              Manage Plans ({catalogReady ? assignedPlans.length : '…'})
            </Button>
          )}
          <Button
            size="sm"
            variant="link"
            colorScheme="blue"
            onClick={onToggleExpand}
          >
            <Flex gap="0.375rem" align="center">
              <Text>{isExpanded ? 'Collapse' : 'Expand'}</Text>
              {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Flex>
          </Button>
        </HStack>
      </Flex>

      <Collapse
        in={canManagePlans && catalogReady && isManageOpen}
        animateOpacity
        style={{
          overflow: catalogReady && isManageOpen ? 'visible' : 'hidden',
        }}
      >
        <Box
          mt="0.75rem"
          bg="background.surfaceTeritiary"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          p="0.75rem 0.875rem"
        >
          <Text fontSize="0.6875rem" fontWeight="600" color="gray.600" mb="0.5rem">
            Plans assigned to {stateName}
          </Text>
          <Flex flexWrap="wrap" gap="0.5rem" mb="0.625rem">
            {assignedPlans.length === 0 && (
              <Text fontSize="0.75rem" color="gray.500" fontStyle="italic">
                No plans assigned to this state yet.
              </Text>
            )}
            {assignedPlans.map((plan) => (
              <Flex
                key={plan.id}
                align="center"
                gap="0.5rem"
                bg="blue.50"
                border="1px solid"
                borderColor="blue.200"
                borderRadius="0.375rem"
                pl="0.625rem"
                pr="0.25rem"
                py="0.25rem"
              >
                <Text fontSize="0.75rem" fontWeight="600" color="blue.700">
                  {plan.friendlyName}
                </Text>
                <IconButton
                  aria-label={`Remove ${plan.friendlyName}`}
                  icon={<CloseIcon boxSize="0.5rem" />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  isLoading={pendingPlanId === plan.id}
                  isDisabled={
                    pendingPlanId !== null && pendingPlanId !== plan.id
                  }
                  onClick={() => requestRemovePlan(plan)}
                />
              </Flex>
            ))}
          </Flex>
          <Box position="relative" maxW="26rem">
            <Input
              size="sm"
              bg="white"
              placeholder={`Search plans to add to ${stateName}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && availablePlans.length > 0 && (
              <Box
                position="absolute"
                top="calc(100% + 0.25rem)"
                left={0}
                right={0}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="0.375rem"
                boxShadow="md"
                zIndex={10}
                maxH="12rem"
                overflowY="auto"
              >
                {availablePlans.map((plan) => (
                  <Flex
                    as="button"
                    type="button"
                    key={plan.id}
                    width="100%"
                    align="center"
                    justify="space-between"
                    px="0.75rem"
                    py="0.5rem"
                    bg="transparent"
                    border="none"
                    textAlign="left"
                    cursor={pendingPlanId ? 'not-allowed' : 'pointer'}
                    opacity={
                      pendingPlanId && pendingPlanId !== plan.id ? 0.5 : 1
                    }
                    disabled={Boolean(pendingPlanId)}
                    _hover={pendingPlanId ? undefined : { bg: 'blue.50' }}
                    _focusVisible={{ bg: 'blue.50', boxShadow: 'outline' }}
                    onClick={() => handleAddPlan(plan)}
                  >
                    <Box>
                      <Text fontSize="0.8125rem" fontWeight="500">
                        {plan.friendlyName}
                      </Text>
                      {plan.companyName && (
                        <Text fontSize="0.6875rem" color="gray.500">
                          {plan.companyName}
                        </Text>
                      )}
                    </Box>
                    {pendingPlanId === plan.id && <Spinner size="xs" />}
                  </Flex>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>

      <Collapse in={isExpanded} animateOpacity>
        <Box mt="0.875rem">
          {loading ? (
            <Flex justify="center" py="2rem">
              <Spinner color="text.brandLight" />
            </Flex>
          ) : processedPlans.length > 0 ? (
            <>
              <Box height="24rem" border="1px solid" borderColor="gray.100" borderRadius="md">
                <ResourceGrid<InsurancePlan>
                  data={processedPlans}
                  columns={memoizedColumns}
                  twoPaneMode={true}
                  leftPaneWidth="35%"
                  rightPaneMinWidth="480px"
                  actionMenuContext={resourceGridContext}
                  actionMenuActions={resourceGridActions}
                />
              </Box>
              <Flex align="center" justify="space-between" mt="0.625rem">
                <Text fontSize="0.6875rem" color="gray.400">
                  Showing {processedPlans.length} of {totalCount} providers
                </Text>
                {totalPages > 1 && (
                  <Box>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </Box>
                )}
              </Flex>
            </>
          ) : sectionError ? (
            <Flex direction="column" align="center" gap="0.5rem" py="1rem">
              <Text fontSize="0.8125rem" color="red.500">
                Failed to load providers for {stateName}.
              </Text>
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                onClick={refetchSectionPlans}
              >
                Retry
              </Button>
            </Flex>
          ) : (
            <Text fontSize="0.8125rem" color="gray.500" py="1rem">
              No providers found for {stateName} with the current filters.
            </Text>
          )}
        </Box>
      </Collapse>

      <AlertDialog
        isOpen={isRemoveConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCloseRemoveConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.600">
              Remove Plan from {stateName}
            </AlertDialogHeader>
            <AlertDialogBody>
              Do you really want to remove{' '}
              <Text as="span" fontWeight="600">
                {planPendingRemoval?.friendlyName}
              </Text>{' '}
              from {stateName} ({stateAbbreviation})? Providers and practices
              in this state will no longer show this plan.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseRemoveConfirm}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmRemovePlan}
                ml={3}
                isLoading={pendingPlanId === planPendingRemoval?.id}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default StatePlansSection;
